import React, { useEffect, useState } from 'react';

/**
 * CockpitStepsBar - compact sticky "Step X of Y" bar for a multi-step
 * checklist locate. Shown directly above the marker stage image:
 *  - on the full Explorer page (both the stacked/mobile layout and the
 *    side-by-side/desktop layout), and
 *  - inside the Assistance panel's locate view when it renders as a
 *    narrow overlay/bottom sheet (not the desktop side-by-side panel,
 *    which keeps the full CockpitChecklistStepsCard instead).
 *
 * Tapping the step text expands the full numbered step list inline;
 * it collapses again automatically once a different step is selected.
 */
export const CockpitStepsBar = (props) => {
  const itemText = props.itemText;
  const phaseTitle = props.phaseTitle;
  const steps = props.steps || [];
  const selectedControlId = props.selectedControlId;
  const onStepClick = props.onStepClick;

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [selectedControlId]);

  if (!steps.length) return null;

  const currentIndex = Math.max(0, steps.findIndex((s) => s.control.id === selectedControlId));
  const currentStep = steps[currentIndex];

  const goTo = (index) => {
    const wrapped = (index + steps.length) % steps.length;
    onStepClick && onStepClick(steps[wrapped]);
  };

  return (
    <div
      className="sticky top-0 z-10 flex-shrink-0 rounded-gc-card border"
      style={{ background: '#0B1820', borderColor: '#1F4A57' }}
      data-testid="cockpit-steps-bar"
    >
      <div className="flex items-center gap-1.5 p-2">
        <button
          type="button"
          onClick={() => goTo(currentIndex - 1)}
          className="flex-shrink-0 font-plex-mono text-xs font-semibold px-2.5 py-2 rounded-gc-button cursor-pointer"
          style={{ color: 'var(--gc-cyan)' }}
          aria-label="Previous step"
          data-testid="cockpit-steps-bar-prev"
        >
          ‹ Prev
        </button>

        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex-1 min-w-0 text-left px-1 cursor-pointer"
          aria-expanded={expanded}
          data-testid="cockpit-steps-bar-toggle"
        >
          <div
            className="font-plex-mono text-[10px] font-semibold tracking-[0.1em]"
            style={{ color: 'var(--gc-text-muted)' }}
          >
            STEP {currentIndex + 1} OF {steps.length}
          </div>
          <div className="text-sm font-medium truncate" style={{ color: 'var(--gc-text-main)' }}>
            {currentStep.text}
          </div>
        </button>

        <button
          type="button"
          onClick={() => goTo(currentIndex + 1)}
          className="flex-shrink-0 font-plex-mono text-xs font-semibold px-2.5 py-2 rounded-gc-button cursor-pointer"
          style={{ color: 'var(--gc-cyan)' }}
          aria-label="Next step"
          data-testid="cockpit-steps-bar-next"
        >
          Next ›
        </button>
      </div>

      {expanded && (
        <div className="flex flex-col gap-1.5 px-2.5 pb-2.5">
          {(itemText || phaseTitle) && (
            <div className="flex flex-col gap-0.5 px-1 pb-0.5">
              {itemText && (
                <div className="font-plex-mono text-[13px] font-semibold" style={{ color: 'var(--gc-text-main)' }}>
                  {itemText}
                </div>
              )}
              {phaseTitle && (
                <div className="font-plex-mono text-[10px]" style={{ color: 'var(--gc-text-muted)' }}>
                  {phaseTitle}
                </div>
              )}
            </div>
          )}
          {steps.map((step, i) => {
            const isSelected = step.control.id === selectedControlId;
            return (
              <button
                key={`${step.control.id}-${i}`}
                type="button"
                onClick={() => onStepClick && onStepClick(step)}
                className="flex items-center gap-2.5 min-h-[40px] px-2.5 rounded-gc-button border text-left cursor-pointer"
                style={{
                  color: 'var(--gc-text-main)',
                  background: isSelected ? 'var(--gc-current-row)' : 'var(--gc-checklist-card)',
                  borderColor: isSelected ? 'var(--gc-cyan)' : 'var(--gc-border-card)',
                }}
                data-testid={`cockpit-steps-bar-step-${i}`}
              >
                <span
                  className="font-plex-mono font-semibold flex items-center justify-center rounded-full flex-shrink-0"
                  style={{
                    width: '22px',
                    height: '22px',
                    fontSize: '11px',
                    background: isSelected ? 'var(--gc-cyan)' : 'transparent',
                    color: isSelected ? '#04161C' : 'var(--gc-cyan)',
                    border: isSelected ? 'none' : '1.5px solid var(--gc-cyan)',
                  }}
                >
                  {i + 1}
                </span>
                <span className="flex-1 text-sm leading-snug">{step.text}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CockpitStepsBar;
